import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import moment from 'moment-timezone';
import { unitOfTime } from 'moment-timezone';
import { Calendar, CalendarModule } from 'primeng/calendar';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { FloatLabelModule } from 'primeng/floatlabel';
import { FRM_FILTERS_UI_V2_PANEL_CLASS, type FiltersUiVersion } from '@zaoblako/web/core/types/filters-ui-version.type';
import { FilterChipAutosizeHelper } from '@zaoblako/web/shared/helpers/filter-chip-autosize.helper';

@Component({
  standalone: true,
  selector: 'crm-daterangepicker',
  templateUrl: './daterangepicker.component.html',
  styleUrls: ['./daterangepicker.component.scss'],
  imports: [CommonModule, CalendarModule, FormsModule, ButtonComponent, FloatLabelModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.frm-filters-ui-v2]': 'filtersUiVersion === "v2"'
  }
})
export class DaterangepickerComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('rangepicker') public readonly rangepicker: Calendar;

  @Output() public valueChange = new EventEmitter<Date>();
  @Input() public showButtonBar = false;
  @Input() public dateFormat = 'dd.mm.yy';
  @Input() public label: string;
  @Input() public inputId = Math.random().toString();
  @Input() public placeholder = '\u00A0';
  @Input() public readonlyInput = true;
  @Input() public appendTo = '';
  @Input() public isShowDay = true;
  @Input() public allowTheSameDay = true;
  @Input() public showClear = false;
  @Output() public changeValue = new EventEmitter<[Date, Date]>();
  @Output() public show = new EventEmitter<any>();
  @Output() public close = new EventEmitter<any>();

  public _value: [Date, Date];

  @Input() public isNotCheckedExternal = false;

  @Input() public filtersUiVersion: FiltersUiVersion = 'v2';

  private timer: ReturnType<typeof setInterval>;
  private chipAutosize: FilterChipAutosizeHelper | null = null;

  public get datepickerPanelClass(): string {
    return this.filtersUiVersion === 'v2' ? FRM_FILTERS_UI_V2_PANEL_CLASS : '';
  }

  @Input()
  public set value(dates: [Date | string, Date | string]) {
    if (dates[0] && dates[1]) {
      this._value = [moment(dates[0]).toDate(), moment(dates[1]).toDate()];
    } else {
      this._value = null;
    }
    this.refreshChipAutosize();
  }

  public get value(): [Date | string, Date | string] {
    return this._value;
  }

  public constructor(
    private readonly renderer2: Renderer2,
    private readonly elementRef: ElementRef<HTMLElement>
  ) {}

  public ngOnInit() {
    this.timer = setInterval(() => {
      if (this.isNotCheckedExternal) {
        (this.rangepicker?.contentViewChild?.nativeElement as HTMLElement)?.querySelectorAll('span')?.forEach((el) => {
          this.renderer2.addClass(el, 'p-datepicker-not-check-external');
        });

        const monthBtn = (this.rangepicker?.contentViewChild?.nativeElement as HTMLElement)?.querySelector(
          '.p-datepicker-month'
        );
        if (monthBtn) {
          this.renderer2.addClass(monthBtn, 'p-datepicker-not-check-external');
        }

        const yearBtn = (this.rangepicker?.contentViewChild?.nativeElement as HTMLElement)?.querySelector(
          '.p-datepicker-year'
        );
        if (yearBtn) {
          this.renderer2.addClass(yearBtn, 'p-datepicker-not-check-external');
        }
      }
    }, 300);
  }

  public ngAfterViewInit(): void {
    this.setupChipAutosize();
  }

  public ngOnDestroy() {
    clearInterval(this.timer);
    this.chipAutosize?.disconnect();
    this.chipAutosize = null;
  }

  public changeDate(dates: [Date, Date]) {
    if (dates?.[0] && dates?.[1]) {
      if (!this.allowTheSameDay && moment(dates[0]).isSame(dates[1])) {
        this._value = [moment(dates[0]).toDate(), moment(dates[0]).add(1, 'days').toDate()];
        this.changeValue.emit(this._value);
      } else this.changeValue.emit(dates);
    } else if (this.showClear) {
      this.changeValue.emit(null);
    }
    this.refreshChipAutosize();
  }

  public setPeriodDates(period: unitOfTime.DurationConstructor, previous = false) {
    const from = moment().startOf(period);
    let to = moment().endOf(period);

    if (previous) {
      from.subtract(1, period);
      to = from.clone().endOf(period);
    }

    this._value = [from.toDate(), to.toDate()];
    this.changeValue.emit(this._value);
    this.rangepicker.toggle();
    this.refreshChipAutosize();
  }

  private setupChipAutosize(): void {
    this.chipAutosize?.disconnect();
    this.chipAutosize = null;

    if (this.filtersUiVersion !== 'v2') {
      return;
    }

    this.chipAutosize = new FilterChipAutosizeHelper(this.elementRef.nativeElement, {
      resolveMeasureText: () => this.getDaterangeMeasureText()
    });
    this.chipAutosize.connect();
  }

  private refreshChipAutosize(): void {
    this.chipAutosize?.scheduleSync();
  }

  private getDaterangeMeasureText(): string | null {
    if (!this._value?.[0] || !this._value?.[1]) {
      return null;
    }
    const input = this.elementRef.nativeElement.querySelector(
      'input.p-inputtext, input.p-component'
    ) as HTMLInputElement | null;
    const fromDom = input?.value?.trim();
    if (fromDom) {
      return fromDom;
    }
    return `${moment(this._value[0]).format('DD.MM.YYYY')} - ${moment(this._value[1]).format('DD.MM.YYYY')}`;
  }
}
